import { useMemo } from 'react';
import { useDepartments } from '@/api/queries/useDepartments';
import { useInitializeUnresolvedDepartments } from '@/api/queries/useInitializeUnresolvedDepartments';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useOrgFunctions } from '@/api/queries/useOrgFunctions';
import { useProcesses } from '@/api/queries/useProcesses';
import { useSystems } from '@/api/queries/useSystems';
import {
  departmentsToDrafts,
  processesToDrafts,
  systemsToDrafts,
  usersToDrafts
} from './utils/existingToDrafts';
import { effectiveAnchorRows, NO_PROTECTED_IDS } from './utils/protectedRows';

/**
 * The display rule of a done anchor step: it shows the seeded (already
 * imported) rows until the first edit lands in the draft state — Weiter then
 * appends new rows, updates edited seeded rows in place and deletes removed
 * ones surgically (ADR 0011, third amendment); untouched, it just navigates.
 * A cleared table (empty draft, surviving flush baseline) stays empty instead
 * of falling back to the seeds — its rows are pending removals until Weiter
 * commits them. Thin protected-row-free view of effectiveAnchorRows, so the
 * display, the submit, and the Weiter-enable gate can never drift apart.
 */
export function anchorDisplayRows<T extends { entityId?: string; rowNumber: number }>(
  drafts: T[],
  seeds: T[],
  isDone: boolean,
  baseline?: T[]
): T[] {
  return effectiveAnchorRows({
    draftRows: drafts,
    seedRows: seeds,
    protectedIds: NO_PROTECTED_IDS,
    isDone,
    baseline
  });
}

/**
 * Single source for the seeded anchor tables AND the Weiter-time
 * classification (classifyAnchorDrafts): both must see identical rows,
 * otherwise an untouched table could be misclassified as edited.
 */
export function useAnchorSeedRows() {
  const { data: existingDepartments, isLoading: departmentsLoading } = useDepartments();
  const { data: unresolvedDepartments, isLoading: unresolvedLoading } =
    useInitializeUnresolvedDepartments();
  // Internal-only mirrors the wizard's import scope (ADR 0011).
  const { data: orgMembers, isLoading: membersLoading } = useOrgMembers({
    status: ['inactive', 'pending', 'active'],
    classification: 'internal'
  });
  // The Stelle's Beschreibung lives on the role OrgFunction; join it back into
  // the seeded user rows so re-entry doesn't lose it.
  const { data: roleOrgFunctions, isLoading: rolesLoading } = useOrgFunctions('role');

  const departmentSeeds = useMemo(() => {
    // useDepartments now returns unresolved rows too, but without managerEmail
    // (feature-contract-only field) — take those from the unresolved endpoint
    // and exclude them from the general list BY ID, trusting the server's
    // classification instead of re-deriving it client-side.
    const unresolved = unresolvedDepartments ?? [];
    const unresolvedIds = new Set(unresolved.map((dept) => dept._id));
    return departmentsToDrafts([
      ...(existingDepartments ?? []).filter((dept) => !unresolvedIds.has(dept._id)),
      ...unresolved
    ]);
  }, [existingDepartments, unresolvedDepartments]);
  const roleDescriptionById = useMemo(
    () => new Map((roleOrgFunctions ?? []).map((fn) => [fn._id, fn.description])),
    [roleOrgFunctions]
  );
  // ALL internal members seed the table — replaceable wizard imports become
  // editable rows, protected members (superadmin-precreated, invited or
  // activated) render as locked-identity rows whose placement commits via the
  // dedicated PATCH (see utils/protectedRows).
  const userSeeds = useMemo(
    () => usersToDrafts(orgMembers ?? [], roleDescriptionById),
    [orgMembers, roleDescriptionById]
  );

  // During setup every system/process stems from the wizard (the org is
  // still gated), so the whole lists seed the editable tables.
  const { data: existingSystems, isLoading: systemsLoading } = useSystems();
  const { data: existingProcesses, isLoading: processesLoading } = useProcesses();
  const systemSeeds = useMemo(() => systemsToDrafts(existingSystems ?? []), [existingSystems]);
  const processSeeds = useMemo(
    () => processesToDrafts(existingProcesses ?? []),
    [existingProcesses]
  );

  // The page gates its first paint on this: an edit before the seed queries
  // resolve would freeze an EMPTY seed list as the flush baseline — the
  // imported rows would never display again this visit, and a confirmed
  // "Liste leeren" delete-all would silently no-op.
  const seedsLoading =
    departmentsLoading ||
    unresolvedLoading ||
    membersLoading ||
    rolesLoading ||
    systemsLoading ||
    processesLoading;

  return { departmentSeeds, userSeeds, systemSeeds, processSeeds, seedsLoading };
}
